import React, { FC, useEffect, useState } from 'react';
import { PrimaryButton } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import {
    useWizard,
    Wizard,
    WizardPivotHeader,
    WizardPivotPanel,
    WizardState,
} from '../common/component/WizardPivot';
import './TaxpayerSelfRegistryMain.scss';
import { TaxpayerSignature } from './TaxpayerSignature';
import { TaxpayerInformation } from './TaxpayerInformation';
import { TaxpayerVerification } from './TaxpayerVerification';
import { InviteAdministrator } from './InviteAdministrator';
import { LanguageTypes, NewSessionResponse } from './Service/TaxpayerProxy';

export interface Props {
    onClose: () => void;
    onSuccessRegister: (
        rin: string,
        primaryName: string,
        secondaryName: string,
        email: string,
    ) => void;
}

export enum itemsKey {
    Signature = 'TaxpayerSignature',
    Information = 'TaxpayerInformation',
    Verification = 'Verification',
    Invite = 'InviteAdministrator',
}

export const TaxpayerSelfRegistryMain: FC<Props> = (props: Props) => {
    const { onClose, onSuccessRegister } = props;
    const { t } = useTranslation(['SignUp']);

    const [signingFooter, setSigningFooter] = useState<JSX.Element>();
    const [registerTaxpayerFooter, setRegisterTaxpayerFooter] =
        useState<JSX.Element>();
    const [verifyOtpFooter, setverifyOtpFooter] = useState<JSX.Element>();
    const [sendInvitationFooter, setSendInvitationFooter] =
        useState<JSX.Element>();
    const [rin, setRin] = useState<string>();
    const [defaultLanguage, setDefaultLanguage] = useState<LanguageTypes>(
        LanguageTypes.En
    );
    const [currentMobileNo, setCurrentMobileNo] = useState<string>();
    const [newSession, setNewSession] = useState<NewSessionResponse>(
        new NewSessionResponse()
    );
    const [hasDigitalProfile, setHasDigitalProfile] = useState<boolean>(false);
    const [sessionTime, setSessionTime] = useState<number>(-1);

    const taxpayerSelfRegistryWizardWizardState = (
        hideTaxPayerInfo = false
    ): WizardState => {
        return {
            translationNamespace: 'SignUp',
            tabs: [
                {
                    itemKey: itemsKey.Signature,
                    label: 'TaxpayerSignature',
                    notClickable: true,
                    itemState: 'completed',
                },
                {
                    itemKey: itemsKey.Information,
                    label: 'TaxpayerInformation',
                    notClickable: true,
                    hidden: hideTaxPayerInfo,
                },
                {
                    itemKey: itemsKey.Verification,
                    label: 'Verification',
                    notClickable: true,
                    hidden: hideTaxPayerInfo,
                },
                {
                    itemKey: itemsKey.Invite,
                    label: 'InviteAdministrator',
                    notClickable: true,
                },
            ],
            selectedTabKey: 'TaxpayerSignature',
            showTabState: true,
            canGoBack: false,
            canGoNext: true,
        };
    };

    const taxpayerSelfRegistryWizard = useWizard(
        taxpayerSelfRegistryWizardWizardState()
    );

    const getFooterAction = () => {
        switch (taxpayerSelfRegistryWizard.state.selectedTabKey) {
            case itemsKey.Signature:
                return signingFooter;
            case itemsKey.Information:
                return registerTaxpayerFooter;
            case itemsKey.Verification:
                return verifyOtpFooter;
            case itemsKey.Invite:
                return sendInvitationFooter;
            default:
                return <></>;
        }
    };

    const rendersessionTimer = (time: number) => {
        let second = (time % 60).toString();
        let minute = Math.floor(time / 60).toString();
        minute =
            minute.toString().length === 1 ? `0${minute}` : minute.toString();
        second =
            second.toString().length === 1 ? `0${second}` : second.toString();
        return `${minute}m:${second}s`;
    };

    useEffect(() => {
        setSessionTime(-1);
        if (newSession?.sessionExpiryInMin)
            setSessionTime(newSession?.sessionExpiryInMin * 60);
    }, [newSession]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let intervalId: any;
        if (sessionTime > 0) {
            intervalId = setInterval(
                () => setSessionTime(sessionTime - 1),
                1000
            );
        } else if (!!newSession?.sessionExpiryInMin && sessionTime === 0) {
            onClose();
        }
        return () => clearInterval(intervalId);
    });

    return (
        <div id="signUpHeader">
            <Wizard state={taxpayerSelfRegistryWizard}>
                <div className="header">
                    <div className="title-content">
                        <p>{t('SelfRegistration1')}</p>
                        <p>{t('SelfRegistration2')}</p>
                    </div>
                    <WizardPivotHeader />
                </div>
                <div className="content">
                    <WizardPivotPanel panelKey="TaxpayerSignature">
                        <div className="wizardPanelHeading">
                            <p>{`1. ${t('NumberAndSignature')}`}</p>
                        </div>
                        <TaxpayerSignature
                            setFooter={(value: JSX.Element) =>
                                setSigningFooter(value)
                            }
                            taxpayerSelfRegistryWizard={
                                taxpayerSelfRegistryWizard
                            }
                            setRin={setRin}
                            resetWizard={(hasProfile: boolean) =>{
                                setHasDigitalProfile(hasProfile);
                                taxpayerSelfRegistryWizard.reset(
                                    taxpayerSelfRegistryWizardWizardState(
                                        hasProfile
                                    )
                                );
                            }}
                            setNewSession={setNewSession}
                        />
                    </WizardPivotPanel>
                    <WizardPivotPanel panelKey="TaxpayerInformation">
                        <TaxpayerInformation
                            setFooter={(value: JSX.Element) =>
                                setRegisterTaxpayerFooter(value)
                            }
                            taxpayerSelfRegistryWizard={
                                taxpayerSelfRegistryWizard
                            }
                            rin={rin}
                            setDefaultLanguage={setDefaultLanguage}
                            newSession={newSession}
                            setCurrentMobileNo={setCurrentMobileNo}
                        />
                    </WizardPivotPanel>
                    <WizardPivotPanel panelKey="Verification">
                        <div className="wizardPanelHeading">
                            <p>{`3. ${t('VerifyContact')}`}</p>
                        </div>
                        <TaxpayerVerification
                            setFooter={(value: JSX.Element) =>
                                setverifyOtpFooter(value)
                            }
                            taxpayerSelfRegistryWizard={
                                taxpayerSelfRegistryWizard
                            }
                            newSession={newSession}
                            mobileNo={currentMobileNo ?? ''}
                            defaultLanguage={defaultLanguage}
                        />
                    </WizardPivotPanel>
                    <WizardPivotPanel panelKey="InviteAdministrator">
                        <div className="wizardPanelHeading">
                            <p>{`4. ${t('InviteAdmin')}`}</p>
                        </div>
                        <InviteAdministrator
                            setFooter={(footer: JSX.Element) =>
                                setSendInvitationFooter(footer)
                            }
                            setSuccessInvite={(email: string) => {
                                if (email)
                                    onSuccessRegister(
                                        rin ?? '',
                                        newSession?.taxpayerInfo
                                            ?.namePrimaryLang ?? '',
                                        newSession?.taxpayerInfo
                                            ?.nameSecondaryLang ?? '',
                                        email
                                    );
                            }}
                            sessionID={newSession?.sessionId ?? ''}
                            rin={rin ?? ''}
                            defaultLanguage={defaultLanguage}
                            hasDigitalProfile={hasDigitalProfile}
                        />
                    </WizardPivotPanel>
                </div>
                <div className="footer">
                    <div className="navigation">
                        {getFooterAction()}
                        {taxpayerSelfRegistryWizard.state.canGoBack ? (
                            <PrimaryButton
                                className="back"
                                iconProps={{ iconName: 'Back' }}
                                disabled={
                                    !taxpayerSelfRegistryWizard.state
                                        .canGoNext &&
                                    !hasDigitalProfile
                                }
                                text={t('previous')}
                                onClick={() =>
                                    taxpayerSelfRegistryWizard.previousTab()
                                }
                            />
                        ) : (
                            <PrimaryButton
                                className="closeButton"
                                iconProps={{ iconName: 'Cancel' }}
                                text={t('cancel')}
                                onClick={onClose}
                            />
                        )}
                        <div>
                            {t('SessionExpireIn')}
                            {sessionTime > 0 && (
                                <div className="font_Timer">
                                    {' '}
                                    {rendersessionTimer(sessionTime)}{' '}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Wizard>
        </div>
    );
};
